import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { requireAdmin } from "@/auth/requireAdmin";
import { useEffect, useRef, useState } from "react";
import { Button, PageContent } from "beproduct-ui";
import { Eye } from "lucide-react";
import { PageHeader } from "@/components/navigation/page-header";
import jQuery from "jquery";
// Vite resolves `?url` to a served URL for the asset, leaving the file
// itself out of our ESM graph. We then load Kendo's UMD bundle via a
// `<script>` tag inside the effect — the UMD wrapper detects `window`
// and registers `kendo` + `jQuery.fn.kendo*` correctly. The ESM build
// (what `import("@progress/kendo-ui")` would resolve to) does NOT
// side-effect-extend jQuery, which is why direct loads failed.
import kendoUmdUrl from "@progress/kendo-ui/umd/kendo.all.min.js?url";

// Telerik's HTML5 Web Designer is a jQuery widget. The JS + CSS aren't
// bundled in any npm package — we fetch them from the .NET service's
// `designerresources/` endpoints (which the Telerik controller serves
// from its embedded assembly resources).
if (typeof window !== "undefined") {
  const w = window as unknown as { jQuery?: unknown; $?: unknown };
  if (!w.jQuery) w.jQuery = jQuery;
  if (!w.$) w.$ = jQuery;
}

// Telerik version baked into the resource filenames. If we bump the
// `Telerik.WebReportDesigner.Services` package, update this string and
// re-test.
const TR_VERSION = "20.0.26.424";

export const Route = createFileRoute("/_authed/$companySlug/reports/$reportId/edit")({
  beforeLoad: ({ context, params }) => {
    requireAdmin(context, params.companySlug, `/${params.companySlug}/reports/${params.reportId}`);
  },
  component: ReportDesignerPage,
});

function ReportDesignerPage() {
  const { company } = Route.useRouteContext();
  const { companySlug, reportId } = Route.useParams();
  const nav = useNavigate({ from: "/$companySlug/reports/$reportId/edit" });
  const hostRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);


  // Designer REST root, through the Fastify proxy. Trailing slash matters
  // — the designer client appends `clients`, `resources`, etc.
  const designerBase = `/api/tenants/${encodeURIComponent(company.id)}/reports/api/reportdesigner/`;
  // Viewer REST root (for previews from inside the designer).
  const viewerBase = `/api/tenants/${encodeURIComponent(company.id)}/reports/api/reports`;

  useEffect(() => {
    let cancelled = false;
    const linkEls: HTMLLinkElement[] = [];
    let scriptEl: HTMLScriptElement | null = null;

    function injectLink(href: string): Promise<void> {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector<HTMLLinkElement>(`link[data-trd-href="${href}"]`);
        if (existing) return resolve();
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.setAttribute("data-trd-href", href);
        link.onload = () => resolve();
        link.onerror = () => reject(new Error(`stylesheet ${href} failed`));
        document.head.appendChild(link);
        linkEls.push(link);
      });
    }

    function injectScript(src: string): Promise<void> {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(`script[data-trd-src="${src}"]`);
        if (existing) return resolve();
        const s = document.createElement("script");
        s.src = src;
        s.async = false;
        s.setAttribute("data-trd-src", src);
        s.onload = () => resolve();
        s.onerror = () => reject(new Error(`script ${src} failed`));
        document.head.appendChild(s);
        scriptEl = s;
      });
    }

    (async () => {
      try {
        // Order matters — Kendo and jQuery UI both extend the jQuery
        // object on `window`, so we load the UMD bundles via <script>
        // tags rather than ESM imports. UMD's window-detect branch
        // registers `kendo` + `jQuery.fn.kendo*` synchronously.
        await injectScript(kendoUmdUrl);

        // The UMD bundle bypasses @progress/kendo-licensing's bundled
        // activation, so we hand-feed the Kendo UI Script Key here.
        // The Script Key is a distinct obfuscated string available on
        // the Telerik portal under "View Script Keys" — NOT the raw
        // license JWT. Without it, Kendo's validatePackage call fires
        // a "License key missing" popup AND short-circuits widget
        // setup, which is why the designer then fails to register
        // `$.fn.telerik_WebReportDesigner` downstream.
        const scriptKey = (import.meta.env.VITE_TELERIK_SCRIPT_KEY ?? "").trim();
        const w = window as unknown as {
          kendo?: { licensing?: { setScriptKey?: (k: string) => void } };
          KendoLicensing?: { setScriptKey?: (k: string) => void };
        };

        // DEBUG: what licensing surfaces are available, and where?
        console.log("[kendo-license probe]", {
          scriptKeyLen: scriptKey.length,
          windowKendo: typeof w.kendo,
          windowKendoLicensing: typeof w.kendo?.licensing,
          kendoLicensingSetScriptKey: typeof w.kendo?.licensing?.setScriptKey,
          globalKendoLicensing: typeof w.KendoLicensing,
          globalSetScriptKey: typeof w.KendoLicensing?.setScriptKey,
        });

        // Call BOTH (the UMD's inlined kendo-licensing publishes both
        // shapes; calling whichever exists covers either).
        let called = 0;
        if (scriptKey && w.kendo?.licensing?.setScriptKey) {
          w.kendo.licensing.setScriptKey(scriptKey);
          called++;
        }
        if (scriptKey && w.KendoLicensing?.setScriptKey) {
          w.KendoLicensing.setScriptKey(scriptKey);
          called++;
        }
        console.log("[kendo-license] setScriptKey called", called, "place(s)");

        await injectScript(
          `${designerBase}designerresources/ext_js/jquery.ui-1.14.1.min.js`,
        );

        await Promise.all([
          injectLink(`${designerBase}designerresources/ext_styles/jquery.ui-1.14.1.min.css`),
          injectLink(`${designerBase}designerresources/ext_styles/webReportDesignerTheme-${TR_VERSION}.min.css`),
          injectLink(`${designerBase}designerresources/styles/webReportDesigner-${TR_VERSION}.min.css`),
        ]);

        await injectScript(
          `${designerBase}designerresources/js/webReportDesigner-${TR_VERSION}.min.js`,
        );
        if (cancelled) return;

        // Clear only the keys that drive "which report to auto-open".
        // Telerik writes these unprefixed keys to localStorage:
        //   LastOpenedReport, PreviouslyOpenedReports, RecentReports
        // — without this clear the designer ignores our `report` option
        // and restores whatever was loaded last. We deliberately leave
        // other Telerik state (StartedOnboardingGuides, ToolboxAreaLayout,
        // SendAnonymousUsageData) alone so the user doesn't get the tour
        // re-prompted on every reload.
        const TELERIK_REPORT_URI_KEYS = [
          "LastOpenedReport",
          "PreviouslyOpenedReports",
          "RecentReports",
        ];
        for (const store of [window.localStorage, window.sessionStorage]) {
          for (const k of TELERIK_REPORT_URI_KEYS) store.removeItem(k);
        }

        const $host = jQuery(hostRef.current!);
        const widget = ($host as unknown as { telerik_WebReportDesigner: (o: unknown) => void });
        widget.telerik_WebReportDesigner({
          serviceUrl: designerBase,
          reportServiceUrl: viewerBase,
          // .trdx = raw-XML format; .trdp would be the ZIP-packaged
          // form (which Telerik's storage parses as a binary archive).
          // We persist raw XML in `rpt_report.definition.xml`, so .trdx.
          report: `${reportId}.trdx`,
          toolboxArea: { layout: "list" },
          // Off so future route changes don't re-introduce the issue.
          persistSession: false,
        });
        // Hide the Asset Manager + the file-browser "Open" affordances
        // — the designer's storage is per-tenant on disk, but its
        // Asset Manager UI walks the directory tree without our
        // tenant scoping, which would leak file names across tenants.
        // CSS-only because Telerik's config doesn't expose a single
        // switch in this version; the upload + replace flow from the
        // Reports list page covers the legitimate use case.
        hideAssetManagerUi();
        setStatus("ready");
      } catch (e) {
        setErrMsg(e instanceof Error ? e.message : String(e));
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      // The designer widget mutates the host element heavily; rather than
      // try to .destroy() (varies by version), wipe the host on unmount.
      if (hostRef.current) hostRef.current.innerHTML = "";
      // Leave script/link tags in place — they're idempotent on remount.
    };
  }, [designerBase, viewerBase, reportId]);

  return (
    <PageContent>
      <div className="pt-4 pb-4 md:pt-6 space-y-3">
        <PageHeader compact title="Report designer"
          actions={<>
            <span className="hidden lg:inline text-xs text-muted-foreground font-mono">{reportId}</span>
            <Button variant="outline" size="sm" onClick={() => nav({to:"/$companySlug/reports/$reportId",params:{companySlug,reportId}})}><Eye className="size-4" /> View</Button>
          </>}
        />
        {status === "loading" && (
          <div className="text-sm text-muted-foreground">Loading designer…</div>
        )}
        {status === "error" && (
          <div className="text-sm text-destructive">Failed to load designer: {errMsg}</div>
        )}
        {/* Light-mode bubble: the Telerik designer's CSS assumes a
            white page. We don't strip `.dark` from <html> (that'd
            black-out the rest of the app's chrome) — instead we force
            light colors locally. `color-scheme: light` flips form
            controls + scrollbars to light variants, and resetting
            `background` / `color` shields the designer from Tailwind
            dark-mode tokens that descendants might otherwise inherit. */}
        <div
          ref={hostRef}
          id="reportDesigner"
          className="trv-light-host"
          style={{
            position: "relative",
            height: "calc(100vh - 140px)",
            background: "#ffffff",
            color: "#000000",
            colorScheme: "light",
          }}
        />
      </div>
    </PageContent>
  );
}

/**
 * Inject a one-shot <style> that hides the Telerik designer's Asset
 * Manager + Open-file entries. Re-runs of the function dedup via the
 * sentinel data attribute. CSS-only — Telerik's config in this version
 * doesn't expose a single switch, but the menu items are stable enough
 * across versions to target by text.
 *
 * Selectors:
 *   - .trd-menu-item[data-action="assetsManager"] — explicit menu hook
 *     used by Telerik 20.x for "Asset Manager".
 *   - .trd-menu-item[data-action="open"] — "Open" file dialog (also
 *     leaks asset paths across tenants).
 *   - :is() fallback for older builds that label by title.
 */
function hideAssetManagerUi(): void {
  if (typeof document === "undefined") return;
  if (document.head.querySelector("style[data-trd-hide-assets='1']")) return;
  const style = document.createElement("style");
  style.setAttribute("data-trd-hide-assets", "1");
  style.textContent = `
    /* Asset Manager entry in the designer's hamburger / file menu */
    .trd-menu-item[data-action="assetsManager"],
    .trd-menu-item[data-action="openAssetsManager"],
    .trd-menu-item[data-action="open"],
    [data-trd-action="assetsManager"],
    [data-trd-action="open"],
    .trd-menu-item:has(span:is([title*="Asset Manager" i], :--text-asset-manager)) {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
}
